<script setup>
import { computed } from 'vue';

const props = defineProps({
  // Receita ja consumada (cards ganhos) — salesMetrics.totalRevenue
  closedRevenue: { type: Number, default: 0 },
  // forecast_pipeline: { weighted, gross, byStage }
  forecast: { type: Object, default: () => ({}) },
  title: { type: String, default: 'Receita Fechada × Pipeline' },
});

const formatCurrency = value =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value || 0);

const gross = computed(() => Number(props.forecast?.gross || 0));
const weighted = computed(() => Number(props.forecast?.weighted || 0));

// SiteUP CRM (relatorios r2, item A): sem etapa de ganho marcada nao ha
// previsao. O backend manda `configured: false` e a lista de funis que faltam
// configurar. A tela mostra a frase, nunca R$ 0,00 nem uma barra em verde.
const isUnconfigured = computed(() => props.forecast?.configured === false);
const unconfiguredFunnels = computed(() =>
  Array.isArray(props.forecast?.unconfiguredFunnels)
    ? props.forecast.unconfiguredFunnels.filter(Boolean)
    : []
);
// Previsao parcial: parte dos funis projeta, parte nao. O aviso fica junto dos
// numeros para ninguem ler o pipeline como se fosse a conta inteira.
const hasPartialGap = computed(
  () => !isUnconfigured.value && unconfiguredFunnels.value.length > 0
);

// "Confianca do pipeline": quanto do valor em aberto a previsao ponderada estima
// que de fato fecha (weighted / gross). Da a leitura de quanto do pipeline e solido.
const confidencePct = computed(() =>
  gross.value > 0 ? Math.round((weighted.value / gross.value) * 100) : 0
);

// byStage ordenado por valor ponderado desc (onde esta o dinheiro provavel)
const byStage = computed(() => {
  const raw = props.forecast?.byStage || {};
  return Object.entries(raw)
    .map(([stage, v]) => ({
      stage,
      gross: Number(v?.gross || 0),
      weighted: Number(v?.weighted || 0),
      count: Number(v?.count || 0),
      probability: Number(v?.probability || 0),
    }))
    .sort((a, b) => b.weighted - a.weighted);
});

const hasData = computed(() => gross.value > 0 || props.closedRevenue > 0);
</script>

<template>
  <div class="rounded-2xl border border-n-weak bg-n-solid-1 p-3">
    <h3 class="text-sm font-medium text-n-slate-12 mb-2">
      {{ title }}
    </h3>

    <div
      v-if="isUnconfigured"
      class="flex items-start gap-2 rounded-xl bg-n-alpha-2 p-3"
      data-testid="forecast-unconfigured"
    >
      <i class="i-ph-warning-circle mt-0.5 shrink-0 text-n-slate-11" />
      <div class="text-sm text-n-slate-11">
        <p class="font-medium text-n-slate-12">
          Falta marcar a etapa de ganho para prever pipeline
        </p>
        <p class="mt-0.5">
          A previsão só existe quando o funil diz qual etapa é venda fechada.
          Marque a etapa de ganho nas configurações do funil
          <template v-if="unconfiguredFunnels.length">
            ({{ unconfiguredFunnels.join(', ') }}) </template
          >.
        </p>
      </div>
    </div>

    <div v-else-if="!hasData" class="text-sm text-n-slate-10 py-3 text-center">
      Nenhum dado de receita/pipeline no período
    </div>

    <template v-else>
      <p
        v-if="hasPartialGap"
        class="mb-3 text-xs text-n-slate-11"
        data-testid="forecast-partial-gap"
      >
        Fora da previsão, sem etapa de ganho marcada:
        {{ unconfiguredFunnels.join(', ') }}.
      </p>

      <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <!-- Receita Fechada -->
        <div class="rounded-xl bg-green-50 dark:bg-green-900/20 p-3">
          <p class="text-xs font-medium text-green-700 dark:text-green-400">
            Receita Fechada
          </p>
          <p
            class="text-2xl font-extrabold text-green-800 dark:text-green-300 mt-1"
          >
            {{ formatCurrency(closedRevenue) }}
          </p>
          <p class="text-xs text-green-600/70 dark:text-green-500/70 mt-0.5">
            já entrou (ganhos)
          </p>
        </div>

        <!-- Pipeline Ponderado -->
        <div class="rounded-xl bg-blue-50 dark:bg-blue-900/20 p-3">
          <p class="text-xs font-medium text-blue-700 dark:text-blue-400">
            Pipeline Ponderado
          </p>
          <p
            class="text-2xl font-extrabold text-blue-800 dark:text-blue-300 mt-1"
          >
            {{ formatCurrency(weighted) }}
          </p>
          <p class="text-xs text-blue-600/70 dark:text-blue-500/70 mt-0.5">
            previsão (peso por etapa)
          </p>
        </div>

        <!-- Pipeline Bruto -->
        <div class="rounded-xl bg-n-alpha-2 p-3">
          <p class="text-xs font-medium text-n-slate-11">Pipeline Bruto</p>
          <p class="text-2xl font-extrabold text-n-slate-12 mt-1">
            {{ formatCurrency(gross) }}
          </p>
          <p class="text-xs text-n-slate-10 mt-0.5">total em aberto</p>
        </div>
      </div>

      <!-- Barra de confiança do pipeline -->
      <div v-if="gross > 0" class="mt-4">
        <div
          class="flex items-center justify-between text-xs text-n-slate-10 mb-1"
        >
          <span>Confiança do pipeline (ponderado ÷ bruto)</span>
          <span class="font-semibold text-blue-700 dark:text-blue-400"
            >{{ confidencePct }}%</span
          >
        </div>
        <div class="h-2 rounded-full bg-n-alpha-2 overflow-hidden">
          <div
            class="h-2 rounded-full bg-blue-500 transition-all"
            :style="{ width: `${Math.min(confidencePct, 100)}%` }"
          />
        </div>
      </div>

      <!-- Onde está o dinheiro provável (por etapa) -->
      <div v-if="byStage.length" class="mt-4">
        <p class="text-xs font-medium text-n-slate-10 mb-2">
          Pipeline ponderado por etapa
        </p>
        <div class="space-y-1.5">
          <div
            v-for="row in byStage"
            :key="row.stage"
            class="flex items-center justify-between text-sm"
          >
            <span class="text-n-slate-11 truncate pr-2">
              {{ row.stage }}
              <span class="text-xs text-n-slate-10">
                ({{ row.count }} · {{ Math.round(row.probability * 100) }}%)
              </span>
            </span>
            <span class="text-n-slate-12 font-medium whitespace-nowrap">
              {{ formatCurrency(row.weighted) }}
            </span>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
